import type { CancelPlanModalProps } from "../AppModalTypes";

import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionDetails } from "@editor/hooks/subscription";
import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useModal } from "@editor/hooks/useModal";
import { analytics } from "@editor/infra/analytics";
import { billingRouter } from "@editor/trpc/billing";

import { getCountOfAdditionalPublishedElementBundlesInSubscription } from "@/features/workspace/v1/v1BillingUtils";
import { getCountOfV2AdditionalShopsInSubscription } from "@/features/workspace/v2/V2BillingUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import SelectableCard from "@replo/design-system/components/card/SelectableCard";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import {
  getV1BillingPlanInfoByTier,
  getV2BillingPlanInfoByTier,
} from "schemas/billing";
import { z } from "zod";

const CANCEL_REASONS = [
  "Store Closing",
  "Poor Support",
  "Too Expensive",
  "Difficult to Learn",
  "Missing Key Features",
  "Found an Alternative",
  "No Ongoing Value",
  "Other Reason",
] as const;

const cancelPlanFormSchema = z.object({
  selectedReason: z.string().min(1, "Please select at least one reason"),
  textFeedback: z.string().optional(),
});

type CancelPlanFormValues = z.infer<typeof cancelPlanFormSchema>;

export const CancelPlanModal: React.FC<CancelPlanModalProps> = ({
  source,
  reploVersionToCancel,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const modal = useModal();
  const { user } = useCurrentUser();
  const { data: activeSubscription } = useSubscriptionDetails();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
  } = useForm<CancelPlanFormValues>({
    resolver: zodResolver(cancelPlanFormSchema),
    mode: "onChange",
    defaultValues: {
      selectedReason: "",
      textFeedback: "",
    },
  });

  const { initializeSubscriptionUpdate } =
    useInitializeSubscriptionUpdateMutation();

  const [currentStep, setCurrentStep] = React.useState<"reasons" | "feedback">(
    "reasons",
  );

  const { data: nextBillingDate } = billingRouter.getNextBillingDate.useQuery(
    workspaceId && activeSubscription?.id
      ? {
          workspaceId,
          subscriptionId: activeSubscription.id,
        }
      : skipToken,
  );

  const formattedEndDate = React.useMemo(() => {
    if (nextBillingDate) {
      try {
        return format(new Date(nextBillingDate), "MMMM do, yyyy");
      } catch {
        return "the end of your current billing period";
      }
    }
    return "the end of your current billing period";
  }, [nextBillingDate]);

  // NOTE (Cole 2025-09-11): There must be an active subscription to submit an update.
  // Otherwise there's a risk that we accidentally change the v2 plan as we change the v1 plan
  if (!activeSubscription || !workspaceId) {
    return null;
  }

  if (reploVersionToCancel === "v1" && !activeSubscription?.v1BillingTier) {
    return null;
  }
  if (reploVersionToCancel === "v2" && !activeSubscription?.v2BillingTier) {
    return null;
  }

  const cancelV1Plan = async () => {
    const currentV2AdditionalShops =
      getCountOfV2AdditionalShopsInSubscription(activeSubscription);

    await initializeSubscriptionUpdate({
      workspaceId,
      v1BillingTier: "free",
      v2BillingTier: activeSubscription.v2BillingTier,
      billingInterval: activeSubscription.billingInterval,
      source,
      v1AdditionalShops: 0,
      v2AdditionalShops: currentV2AdditionalShops,
      additionalPublishedElementBundles: 0,
    });
  };

  const cancelV2Plan = async () => {
    const currentV1AdditionalShops =
      getCountOfV2AdditionalShopsInSubscription(activeSubscription);
    const currentAdditionalPublishedElementBundles =
      getCountOfAdditionalPublishedElementBundlesInSubscription(
        activeSubscription,
      );

    await initializeSubscriptionUpdate({
      workspaceId,
      v1BillingTier: activeSubscription.v1BillingTier,
      v2BillingTier: "free",
      billingInterval: activeSubscription.billingInterval,
      source,
      v1AdditionalShops: currentV1AdditionalShops,
      v2AdditionalShops: 0,
      additionalPublishedElementBundles:
        currentAdditionalPublishedElementBundles,
    });
  };

  const onSubmit = async (formData: CancelPlanFormValues): Promise<void> => {
    analytics.logEvent("billing.downgrade.survey", {
      previousPlan:
        reploVersionToCancel === "v1"
          ? activeSubscription.v1BillingTier
          : activeSubscription.v2BillingTier,
      currentPlan: "free",
      reasons: [formData.selectedReason],
      feedback: formData.textFeedback ?? "",
    });

    if (reploVersionToCancel === "v1") {
      await cancelV1Plan();
    } else {
      await cancelV2Plan();
    }

    modal.closeModal({ type: "cancelPlan" });
  };

  const reasonsStepTitle = user?.firstName
    ? `${user.firstName}, before you go...`
    : "Before You Go...";

  const nameOfPlanBeingCancelled =
    reploVersionToCancel === "v1"
      ? getV1BillingPlanInfoByTier(activeSubscription.v1BillingTier).displayName
      : getV2BillingPlanInfoByTier(activeSubscription.v2BillingTier)
          .displayName;

  const formSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSubmit(onSubmit)(e);
  };

  // NOTE (Cole 2025-10-01): We give a prorated refund if the user is still paying us after cancelling.
  const isUserStillPayingUsAfterCancelling =
    reploVersionToCancel === "v1"
      ? activeSubscription.v2BillingTier !== "free"
      : activeSubscription.v1BillingTier !== "free";

  return (
    <form onSubmit={formSubmit}>
      <Modal
        isOpen={true}
        onOpenChange={(open) => {
          if (!open && !isSubmitting) {
            modal.closeModal({ type: "cancelPlan" });
          }
        }}
        title={
          currentStep === "reasons"
            ? reasonsStepTitle
            : "We're sorry to see you go!"
        }
        size="base"
        data-testid="billing-plan-change-survey"
        footer={
          <div className="flex flex-row justify-end gap-2">
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                if (!isSubmitting) {
                  modal.closeModal({ type: "cancelPlan" });
                }
              }}
            >
              Nevermind
            </Button>

            <Button
              variant="primary"
              size="base"
              onClick={async () => {
                if (isSubmitting) {
                  return;
                }
                if (currentStep === "reasons") {
                  const isValid = await trigger("selectedReason");
                  if (isValid) {
                    setCurrentStep("feedback");
                  }
                } else {
                  void handleSubmit(onSubmit)();
                }
              }}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {currentStep === "reasons" ? "Cancel Plan" : "Finish"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col -mt-4 gap-4">
          {currentStep === "reasons" ? (
            <>
              <p className="text-muted text-base">
                If you don't mind sharing, we'd love to hear why you're leaving
                Replo.
              </p>
              {errors.selectedReason && (
                <InlineAlert variant="error">
                  {errors.selectedReason.message}
                </InlineAlert>
              )}
              <img
                src="/images/billing/cancel-plan-sad-globes.png"
                role="presentation"
                className=""
              />
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name="selectedReason"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {CANCEL_REASONS.map((reason) => (
                        <SelectableCard
                          key={reason}
                          size="base"
                          title={reason}
                          variant="checkbox"
                          isActive={value === reason}
                          onClick={() => {
                            onChange(reason);
                          }}
                        />
                      ))}
                    </>
                  )}
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-muted text-base">
                We appreciate you trying Replo. Share any feedback with us—we'd
                love to see you back someday.
              </p>
              <img
                src="/images/billing/cancel-plan-sad-balloons.png"
                role="presentation"
                className=""
              />
              <Controller
                name="textFeedback"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Textarea
                    size="base"
                    value={value}
                    onChange={onChange}
                    placeholder="Anything you can share about your decision to cancel will be helpful for our small team."
                    autoFocus
                  />
                )}
              />
              {isUserStillPayingUsAfterCancelling ? (
                <InlineAlert variant="info">
                  We'll cancel your Replo {nameOfPlanBeingCancelled} plan
                  immediately and issue a prorated refund.
                </InlineAlert>
              ) : (
                <InlineAlert variant="info">
                  You'll keep access to your Replo {nameOfPlanBeingCancelled}{" "}
                  plan until {formattedEndDate}.
                </InlineAlert>
              )}
            </>
          )}
        </div>
      </Modal>
    </form>
  );
};
